import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../api";
import { percent } from "../analytics";
import { DataTable, downloadCsv } from "../components/DataTable";
import { dayLabel } from "../earlyAccess";
import { addDays, latestDay } from "../growth";
import { errorText, unwrap } from "../problem";
import {
  cohortColumns,
  cohortShare,
  funnelSteps,
  shade,
  type AnalyticsCohorts,
  type AnalyticsFunnel,
} from "../retention";

const COHORT_WEEKS = [8, 12, 26, 52];
const FUNNEL_DAYS = [30, 90, 180, 365];

export function RetentionPage() {
  return (
    <>
      <h1>Retention</h1>
      <section className="stack">
        <Cohorts />
        <Funnel />
      </section>
    </>
  );
}

function Cohorts() {
  const [weeks, setWeeks] = useState(12);
  const cohorts = useQuery({
    queryKey: ["analytics-cohorts", weeks],
    queryFn: async () => unwrap(await api.GET("/analytics/cohorts", { params: { query: { weeks } } })),
    placeholderData: keepPreviousData,
    staleTime: 10 * 60_000,
  });
  return (
    <>
      <div className="section-head">
        <h2>Weekly sign-up cohorts</h2>
        <label className="inline">
          Cohorts{" "}
          <select value={weeks} onChange={(event) => setWeeks(Number(event.target.value))}>
            {COHORT_WEEKS.map((n) => (
              <option key={n} value={n}>
                Last {n} weeks
              </option>
            ))}
          </select>
        </label>
      </div>
      {cohorts.isPending ? (
        <p>Loading…</p>
      ) : cohorts.isError ? (
        <p className="error">{errorText(cohorts.error)}</p>
      ) : cohorts.data.built_through === null ? (
        <p className="muted">The rollup builds cohorts on Sundays; none is built yet.</p>
      ) : (
        <CohortGrid data={cohorts.data} />
      )}
    </>
  );
}

function CohortGrid({ data }: { data: AnalyticsCohorts }) {
  const columns = cohortColumns(data);
  const download = () =>
    downloadCsv(
      `ten20-cohorts-${data.built_through}-${data.weeks}w.csv`,
      ["cohort", "size", "week", "active", "size_that_week", "share"],
      data.cohorts.flatMap((cohort) =>
        cohort.weeks.map((cell, week) => {
          const share = cohortShare(cell);
          return [
            cohort.cohort,
            cohort.size,
            week,
            cell?.active ?? null,
            cell?.size ?? null,
            share === null ? null : Math.round(share * 10_000) / 10_000,
          ];
        }),
      ),
    );
  return (
    <>
      <p className="muted">
        Each row is the people who signed up in a week (Monday to Sunday, UTC); each column the share of them active
        that many weeks later (week 0 is the sign-up week), through the week ending {dayLabel(data.built_through!)}.
        Deleted accounts leave both counts. Hover a cell for its counts.
      </p>
      <div className="table-scroll">
        <table className="cohorts">
          <thead>
            <tr>
              <th>Sign-up week</th>
              <th className="num">People</th>
              {Array.from({ length: columns }, (_, week) => (
                <th key={week} className="num">
                  {week === 0 ? "Week 0" : week}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.cohorts.map((cohort) => (
              <tr key={cohort.cohort}>
                <td>{dayLabel(cohort.cohort)}</td>
                <td className="num">{cohort.size.toLocaleString()}</td>
                {Array.from({ length: columns }, (_, week) => {
                  if (week >= cohort.weeks.length) return <td key={week} />;
                  const cell = cohort.weeks[week]!;
                  const share = cohortShare(cell);
                  const level = shade(share);
                  return (
                    <td
                      key={week}
                      className={`num heat${level === null ? "" : ` heat-${level}`}`}
                      title={
                        cell === null
                          ? "That Sunday was not built"
                          : `${cell.active.toLocaleString()} of ${cell.size.toLocaleString()} active`
                      }
                    >
                      {cell === null ? "·" : cell.size === 0 ? "—" : percent(share)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        <button type="button" className="button-quiet" onClick={download}>
          Download CSV
        </button>
      </p>
    </>
  );
}

function Funnel() {
  const [days, setDays] = useState(90);
  const from = addDays(latestDay(), 1 - days);
  const funnel = useQuery({
    queryKey: ["analytics-funnel", from],
    queryFn: async () => unwrap(await api.GET("/analytics/funnel", { params: { query: { from } } })),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  });
  return (
    <>
      <div className="section-head">
        <h2>Activation funnel</h2>
        <label className="inline">
          Signed up{" "}
          <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
            {FUNNEL_DAYS.map((n) => (
              <option key={n} value={n}>
                In the last {n} days
              </option>
            ))}
          </select>
        </label>
      </div>
      {funnel.isPending ? (
        <p>Loading…</p>
      ) : funnel.isError ? (
        <p className="error">{errorText(funnel.error)}</p>
      ) : (
        <FunnelChart data={funnel.data} />
      )}
    </>
  );
}

function FunnelChart({ data }: { data: AnalyticsFunnel }) {
  const steps = funnelSteps(data);
  const waiting = data.week2_pending;
  return (
    <>
      <p className="muted">
        Accounts created from {dayLabel(data.from)} through {dayLabel(data.to)} that still exist, as they are now. Each
        step counts those who also passed the steps before: in a circle, the server holds a fix from them, they and
        another member with a fix both share in one of their circles, and an active day 7–13 days after signing up.
      </p>
      <ol className="funnel">
        {steps.map((step) => (
          <li key={step.key}>
            <span className="funnel-label">{step.label}</span>
            <svg className="funnel-bar" viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true">
              <rect className="funnel-track" x={0} y={0} width={100} height={10} />
              {step.ofStart !== null && step.ofStart > 0 && (
                <rect className="funnel-fill" x={0} y={0} width={Math.max(0.5, step.ofStart * 100)} height={10} />
              )}
            </svg>
            <span className="funnel-value">
              {step.users.toLocaleString()}
              {step.key === "active_week2" && ` of ${step.base?.toLocaleString() ?? 0} due`}
              {step.ofPrevious !== null && <span className="muted"> · {percent(step.ofPrevious)} of previous</span>}
            </span>
          </li>
        ))}
      </ol>
      {addDays(data.activity_from, -7) > data.from && (
        <p className="muted">
          Week 2 counts only accounts that signed up from {dayLabel(addDays(data.activity_from, -7))}: older activity
          days are no longer kept.
        </p>
      )}
      {waiting > 0 && (
        <p className="muted">
          {waiting.toLocaleString()} more {waiting === 1 ? "is" : "are"} still within their first two weeks (week 2
          counts days through {dayLabel(data.measured_through)}).
        </p>
      )}
      <DataTable
        header={["Step", "People", "Of previous", "Of sign-ups"]}
        rows={steps.map((step) => [
          step.label,
          step.users,
          step.ofPrevious === null ? null : percent(step.ofPrevious),
          step.ofStart === null ? null : `${step.key === "active_week2" ? "≈ " : ""}${percent(step.ofStart)}`,
        ])}
        fileName={`ten20-funnel-${data.from}-${data.to}.csv`}
      />
    </>
  );
}
